import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import type { PaymentReceivedRow } from "@autorepair/contracts";
import { reportsApi } from "../lib/api";
import { formatDate, formatInvoiceNumber, formatMoney, formatWorkOrderNumber, yearAndMake } from "../lib/money";
import { useShopType } from "../lib/theme";
import { Spinner } from "../components/ui-modern";
import { DateRangeFilter } from "../components/DateRangeFilter";
import { describeRange } from "../lib/dateRange";
import { useDateRangeParam } from "../lib/useDateRangeParam";
import {
  Eyebrow,
  ModernButton,
  ModernCard,
  ModernEmptyState,
  ModernErrorBanner,
  ModernPagination,
  ModernTableCard,
  ModernTd,
  ModernTh,
} from "../components/ui-modern";

type SortColumn =
  | "receivedOn"
  | "amount"
  | "method"
  | "reference"
  | "balanceDue"
  | "customerName"
  | "vehicleDescription"
  | "concern"
  | "workOrderNumber"
  | "invoiceNumber";
type SortDirection = "asc" | "desc";
interface SortState {
  column: SortColumn;
  direction: SortDirection;
}

/** "credit_card" -> "Credit card". */
function methodLabel(method: string): string {
  const text = method.replace("_", " ");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

const DEFAULT_SORT: SortState = { column: "receivedOn", direction: "desc" };

const COLUMNS: { key: SortColumn; label: string; align?: "left" | "right" }[] = [
  { key: "receivedOn", label: "Received" },
  { key: "amount", label: "Payment", align: "right" },
  { key: "method", label: "Method" },
  { key: "reference", label: "Ref #" },
  { key: "balanceDue", label: "Balance", align: "right" },
  { key: "customerName", label: "Customer" },
  { key: "vehicleDescription", label: "Vehicle" },
  { key: "concern", label: "Concern" },
  { key: "workOrderNumber", label: "Work Order" },
  { key: "invoiceNumber", label: "Invoice" },
];

// Byte-identical to PaymentsReceivedReportPageClassic.tsx's own
// compareRows() — same sort semantics must apply regardless of theme.
function compareRows(a: PaymentReceivedRow, b: PaymentReceivedRow, column: SortColumn): number {
  switch (column) {
    case "receivedOn":
      return a.receivedOn.localeCompare(b.receivedOn);
    case "amount":
      return Number(a.amount) - Number(b.amount);
    case "method":
      return a.method.localeCompare(b.method);
    case "reference":
      return (a.reference ?? "").localeCompare(b.reference ?? "");
    case "balanceDue":
      return Number(a.balanceDue) - Number(b.balanceDue);
    case "customerName":
      return a.customerName.localeCompare(b.customerName);
    case "vehicleDescription":
      return a.vehicleDescription.localeCompare(b.vehicleDescription);
    case "concern":
      return (a.concern ?? "").localeCompare(b.concern ?? "");
    case "workOrderNumber":
      return a.workOrderNumber - b.workOrderNumber;
    case "invoiceNumber":
      return a.invoiceNumber - b.invoiceNumber;
  }
}

/**
 * The modern theme's Payments Received report — see
 * docs/FRONTEND_BACKLOG.md Round 60 and `Bay2Bill Redesign.dc.html` lines
 * ~685-745 ("Payments received"). Owner-only, same as
 * PaymentsReceivedReportPageClassic.tsx — that gate lives entirely in the
 * API (ReportsController rejects any other role), and Classic's own file
 * has no frontend role check of its own to preserve, so neither does this.
 *
 * Same data/sort state as PaymentsReceivedReportPageClassic.tsx — same
 * query key, the same client-side `compareRows`/`sort` logic (copied
 * verbatim, not re-derived), duplicated here as a thin re-run of the same
 * `useQuery` (see WorkOrdersListPageModern.tsx's own comment on why
 * duplication over extraction for a page with no mutations of its own).
 */
export function PaymentsReceivedReportPageModern() {
  const hasVehicles = useShopType() !== "detailing";
  const columns = useMemo(() => (hasVehicles ? COLUMNS : COLUMNS.filter((c) => c.key !== "vehicleDescription")), [hasVehicles]);
  const [range, setRange] = useDateRangeParam("thisYear", () => setPage(1));
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const { data, isLoading, error } = useQuery({
    queryKey: ["reports", "payments-received", range.from, range.to],
    queryFn: () => reportsApi.paymentsReceived(range),
  });

  const sortedRows = useMemo(() => {
    if (!data) return [];
    const rows = [...data.rows];
    rows.sort((a, b) => {
      const cmp = compareRows(a, b, sort.column);
      return sort.direction === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [data, sort]);

  // The API returns every payment for the chosen year in one response (the
  // totals cards/footer below need all of them), so paging is client-side
  // over the already-sorted rows.
  const pagedRows = useMemo(() => sortedRows.slice((page - 1) * pageSize, page * pageSize), [sortedRows, page, pageSize]);

  function toggleSort(column: SortColumn): void {
    setPage(1);
    setSort((prev) =>
      prev.column === column
        ? { column, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { column, direction: "asc" },
    );
  }

  const isDefaultSort = sort.column === DEFAULT_SORT.column && sort.direction === DEFAULT_SORT.direction;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3.5">
        <div>
          <Eyebrow className="mb-2 text-[11px] text-[#0f6d86]">Owner report</Eyebrow>
          <h1 className="m-0 text-[28px] font-bold text-[#071a33]">Payments received</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2.5">
          <ModernButton type="button" variant="secondary" onClick={() => {
              setPage(1);
              setSort(DEFAULT_SORT);
            }}
            disabled={isDefaultSort}>
            Reset Sort
          </ModernButton>
        </div>
      </div>

      <DateRangeFilter value={range} onChange={setRange} />

      {isLoading && <Spinner label="Loading payments…" />}
      {error && <ModernErrorBanner message="Could not load payments." />}

      {data && (
        <>
          <div className="mb-4 grid grid-cols-1 gap-3.5 sm:[grid-template-columns:repeat(auto-fit,minmax(190px,1fr))]">
            <ModernCard>
              <Eyebrow className="text-[11px] text-[#6b7a8f]">Collected</Eyebrow>
              <div className="mt-2.5 text-[30px] font-bold leading-none tabular-nums text-[#1e7d32]">
                {formatMoney(data.totalPayments)}
              </div>
              <div className="mt-2 text-[12.5px] text-[#6b7a8f]">{describeRange(range)}</div>
            </ModernCard>
            <ModernCard>
              <Eyebrow className="text-[11px] text-[#6b7a8f]">Still outstanding</Eyebrow>
              <div className="mt-2.5 text-[30px] font-bold leading-none tabular-nums text-[#9a6700]">
                {formatMoney(data.totalBalanceDue)}
              </div>
            </ModernCard>
            <ModernCard>
              <Eyebrow className="text-[11px] text-[#6b7a8f]">Payments recorded</Eyebrow>
              <div className="mt-2.5 text-[30px] font-bold leading-none tabular-nums text-[#071a33]">{data.rows.length}</div>
            </ModernCard>
          </div>

          {data.byMethod.length > 0 && (
            <ModernCard className="mb-4">
              <Eyebrow className="text-[11px] text-[#6b7a8f]">By payment method</Eyebrow>
              <div className="mt-2.5 flex flex-wrap gap-x-8 gap-y-3">
                {data.byMethod.map((m) => (
                  <div key={m.method}>
                    <div className="text-[12.5px] text-[#6b7a8f]">
                      {methodLabel(m.method)} · {m.count}
                    </div>
                    <div className="text-[20px] font-bold leading-tight tabular-nums text-[#071a33]">{formatMoney(m.total)}</div>
                  </div>
                ))}
              </div>
              <p className="m-0 mt-3 text-[12px] text-[#6b7a8f]">
                Pick <strong>Today</strong> to compare with your card terminal's end-of-day report and your cash drawer.
              </p>
            </ModernCard>
          )}

          {data.rows.length === 0 ? (
            <ModernEmptyState>No payments received in this date range. Try a wider range.</ModernEmptyState>
          ) : (
            <ModernTableCard>
              <table className="w-full min-w-[980px] border-collapse text-[13.5px]">
                <thead>
                  <tr className="bg-[#f8fafc]">
                    {columns.map((col) => (
                      <ModernTh key={col.key} align={col.align} onClick={() => toggleSort(col.key)} className="px-3.5 py-[11px]">
                        {col.label}
                        {sort.column === col.key ? (sort.direction === "asc" ? " ▲" : " ▼") : ""}
                      </ModernTh>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pagedRows.map((row) => (
                    <tr key={row.paymentId}>
                      <ModernTd valign="middle" className="whitespace-nowrap px-3.5 py-3 tabular-nums">
                        {formatDate(row.receivedOn)}
                      </ModernTd>
                      <ModernTd
                        valign="middle"
                        align="right"
                        className="whitespace-nowrap px-3.5 py-3 font-bold tabular-nums !text-[#1e7d32]"
                      >
                        {formatMoney(row.amount)}
                      </ModernTd>
                      <ModernTd valign="middle" className="whitespace-nowrap px-3.5 py-3 text-[#4a5361]">
                        {methodLabel(row.method)}
                      </ModernTd>
                      <ModernTd valign="middle" className="whitespace-nowrap px-3.5 py-3 font-mono text-[12.5px] text-[#4a5361]">
                        {row.reference ?? "—"}
                      </ModernTd>
                      <ModernTd
                        valign="middle"
                        align="right"
                        className="whitespace-nowrap px-3.5 py-3 tabular-nums !text-[#9a6700]"
                      >
                        {formatMoney(row.balanceDue)}
                      </ModernTd>
                      <ModernTd valign="middle" className="px-3.5 py-3">
                        <Link to={`/customers/${row.customerNumber}`} className="font-semibold text-[#0f6d86] no-underline hover:underline">
                          {row.customerName}
                        </Link>
                      </ModernTd>
                      {hasVehicles && (
                        <ModernTd valign="middle" className="px-3.5 py-3 text-[#4a5361]">
                          {yearAndMake(row.vehicleDescription)}
                        </ModernTd>
                      )}
                      <ModernTd valign="middle" className="px-3.5 py-3 text-[#4a5361]">
                        {row.concern ?? "—"}
                      </ModernTd>
                      <ModernTd valign="middle" className="whitespace-nowrap px-3.5 py-3">
                        <Link
                          to={`/work-orders/${row.workOrderNumber}`}
                          className="font-mono text-[12.5px] font-medium text-[#0f6d86] no-underline hover:underline"
                          style={{ fontFamily: "'Roboto Mono', ui-monospace, monospace" }}
                        >
                          {formatWorkOrderNumber(row.workOrderNumber)}
                        </Link>
                      </ModernTd>
                      <ModernTd valign="middle" className="whitespace-nowrap px-3.5 py-3">
                        <Link
                          to={`/invoices/${row.invoiceNumber}`}
                          className="font-mono text-[12.5px] font-medium text-[#0f6d86] no-underline hover:underline"
                          style={{ fontFamily: "'Roboto Mono', ui-monospace, monospace" }}
                        >
                          {formatInvoiceNumber(row.invoiceNumber)}
                        </Link>
                      </ModernTd>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-[#f8fafc] font-bold text-[#071a33]">
                    <td className="px-3.5 py-3" style={{ borderTop: "1px solid #e2e7ee" }}>
                      Totals
                    </td>
                    <td
                      className="whitespace-nowrap px-3.5 py-3 text-right tabular-nums text-[#1e7d32]"
                      style={{ borderTop: "1px solid #e2e7ee" }}
                    >
                      {formatMoney(data.totalPayments)}
                    </td>
                    <td colSpan={2} style={{ borderTop: "1px solid #e2e7ee" }} />
                    <td
                      className="whitespace-nowrap px-3.5 py-3 text-right tabular-nums text-[#9a6700]"
                      style={{ borderTop: "1px solid #e2e7ee" }}
                    >
                      {formatMoney(data.totalBalanceDue)}
                    </td>
                    <td colSpan={columns.length - 5} style={{ borderTop: "1px solid #e2e7ee" }} />
                  </tr>
                </tfoot>
              </table>
              <ModernPagination
                page={page}
                pageSize={pageSize}
                total={sortedRows.length}
                onPageChange={setPage}
                onPageSizeChange={(n) => {
                  setPageSize(n);
                  setPage(1);
                }}
              />
            </ModernTableCard>
          )}
        </>
      )}
    </div>
  );
}
